<template>
  <Head title="New Order" />

  <!-- No agents or viewer: straight to manual form -->
  <div v-if="!canImport || isManual" class="mx-auto max-w-2xl py-6">
    <button
      v-if="canImport && isManual"
      class="text-navy-500 hover:text-navy-700 mb-4 cursor-pointer text-sm transition-colors"
      @click="reset()"
    >
      &larr; Back to source selection
    </button>
    <Form
      :order="formOrder"
      :members="membersList"
      submit-text="Create Order"
      :is-submitting="isSubmitting"
      :form-errors="formErrors"
      @on-submit="handleSubmit"
    />
  </div>

  <!-- Import wizard -->
  <template v-else>
    <!-- Source selection -->
    <ImportSourceSelection
      v-if="currentStep === 'source'"
      :agents="connectedAgents"
      @select-manual="onSelectManual"
      @select-agent="onSelectAgent"
    />

    <!-- Agent import steps -->
    <div v-else class="mx-auto max-w-2xl space-y-8 py-6">
      <ImportStepIndicator :current-step="stepIndex" />

      <ImportFileNumber
        v-if="currentStep === 'file-number'"
        :agent="selectedAgent!"
        @back="onBackToSource"
        @continue="onOrderConfirmed"
      />

      <ImportPartySelection
        v-else-if="currentStep === 'parties'"
        :parties="fetchedOrder!.Parties"
        :selected-indices="selectedPartyIndices"
        :verification-enabled="verificationEnabled"
        :document-email-enabled="documentEmailEnabled"
        @toggle-party="toggleParty"
        @toggle-verification="setVerificationEnabled"
        @toggle-document-email="setDocumentEmailEnabled"
        @back="resetFromStep('file-number')"
        @continue="goToStep('documents')"
      />

      <ImportDocumentSelection
        v-else-if="currentStep === 'documents'"
        :agent="selectedAgent!"
        :file-number="fetchedOrder!.FileNumber"
        :fetched-documents="fetchedDocuments"
        :selected-document-ids="selectedDocumentIds"
        @documents-fetched="setFetchedDocuments"
        @toggle-document="toggleDocument"
        @toggle-folder="toggleFolder"
        @select-all="selectAllDocuments"
        @deselect-all="deselectAllDocuments"
        @back="goToStep('parties')"
        @continue="goToStep('review')"
      />

      <ImportReview
        v-else-if="currentStep === 'review'"
        :fetched-order="fetchedOrder!"
        :selected-parties="selectedParties"
        :selected-documents="selectedDocuments"
        :verification-enabled="verificationEnabled"
        :document-email-enabled="documentEmailEnabled"
        :members="membersList"
        @back="goToStep('documents')"
        @submit="onImportSubmit"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { useToast } from "primevue/usetoast"
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue"

import ImportDocumentSelection from "../../components/order/ImportDocumentSelection.vue"
import ImportFileNumber from "../../components/order/ImportFileNumber.vue"
import ImportPartySelection from "../../components/order/ImportPartySelection.vue"
import ImportReview from "../../components/order/ImportReview.vue"
import ImportSourceSelection from "../../components/order/ImportSourceSelection.vue"
import ImportStepIndicator from "../../components/order/ImportStepIndicator.vue"
import { useImportWizard } from "../../composables/useImportWizard"
import { useOrderMutations } from "../../composables/useOrderMutations"
import Form from "./Form.vue"
import type {
  CompanyWithMembers,
  ConnectedAgent,
  CurrentMember,
  FetchedOrderData,
  OrderFormProps,
  OrderFormType,
} from "./types"

const toast = useToast()
const { createOrder } = useOrderMutations(toast)

const props = defineProps<{
  company: CompanyWithMembers
  currentMember: CurrentMember
}>()

const {
  selectedAgent,
  fetchedOrder,
  fetchedDocuments,
  selectedDocumentIds,
  currentStep,
  isManual,
  selectedPartyIndices,
  verificationEnabled,
  documentEmailEnabled,
  stepIndex,
  selectedParties,
  selectedDocuments,
  reset,
  selectAgent,
  selectManual,
  resetFromStep,
  setFetchedOrder,
  setFetchedDocuments,
  toggleParty,
  setVerificationEnabled,
  setDocumentEmailEnabled,
  toggleDocument,
  toggleFolder,
  selectAllDocuments,
  deselectAllDocuments,
  goToStep,
  importOrder,
} = useImportWizard(toast)

// ─── Browser history integration ──────────────────────────────

const onPopState = (event: PopStateEvent) => {
  const wizardStep = event.state?.wizardStep
  if (wizardStep === "source") {
    reset()
  } else if (wizardStep === "manual") {
    selectManual()
  } else if (wizardStep === "file-number") {
    // Go back to file number but we lost the agent ref — go to source
    resetFromStep("source")
  } else if (wizardStep === "parties") {
    resetFromStep("parties")
  } else if (wizardStep === "documents") {
    goToStep("documents")
  } else if (wizardStep === "review") {
    goToStep("review")
  }
}

onMounted(() => {
  reset()
  // Replace current history entry with source step
  window.history.replaceState({ wizardStep: "source" }, "")
  window.addEventListener("popstate", onPopState)
})

onUnmounted(() => {
  window.removeEventListener("popstate", onPopState)
})

// Push history when wizard step changes
watch([currentStep, isManual], ([step, manual], [prevStep, prevManual]) => {
  if (step === prevStep && manual === prevManual) return
  const wizardStep = manual ? "manual" : step
  window.history.pushState({ wizardStep }, "")
})

// ─── Computed ──────────────────────────────────────────────────

const connectedAgents = computed(
  () => props.company.connectedAgentRegistrations || [],
)

const canImport = computed(
  () =>
    connectedAgents.value.length > 0 &&
    (props.currentMember.role === "CONTRIBUTOR" ||
      props.currentMember.role === "ADMIN"),
)

const membersList = computed(() => props.company.members)

const formOrder = computed<OrderFormProps>(() => ({
  description: null,
  externalFileNumber: "",
  officer: {
    id: membersList.value[0]?.id || "",
    name: membersList.value[0]?.name || "",
  },
  verificationEnabled: false,
  documentEmailEnabled: false,
}))

// ─── Wizard Navigation ────────────────────────────────────────

const onSelectManual = () => {
  selectManual()
}

const onSelectAgent = (agent: ConnectedAgent) => {
  selectAgent(agent)
}

const onBackToSource = () => {
  resetFromStep("source")
}

const onOrderConfirmed = (data: FetchedOrderData) => {
  setFetchedOrder(data)
  goToStep("parties")
}

// ─── Import Submission ────────────────────────────────────────

const onImportSubmit = (data: { officerId: string }) => {
  importOrder(props.company.id, data.officerId || null)
}

// ─── Manual Form Submission ───────────────────────────────────

const isSubmitting = ref(false)
const formErrors = reactive<Record<string, string>>({})

const handleSubmit = async (formData: OrderFormType) => {
  isSubmitting.value = true
  Object.keys(formErrors).forEach((key) => delete formErrors[key])

  try {
    const result = await createOrder({
      companyId: props.company.id,
      officerId: formData.officerId || null,
      description: formData.description || null,
      externalFileNumber: formData.externalFileNumber || null,
      verificationEnabled: formData.verificationEnabled,
      documentEmailEnabled: formData.documentEmailEnabled,
    })

    if (result.success && result.data) {
      router.visit(`/orders/${result.data.gid}`)
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>
